import { Heading } from '@/components/common/Heading';
import type { Protocol } from '@/domain/Protocol';
import { cn } from '@/lib/cn';
import { routes } from '@/routes/routes';
import { Link } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type FormulaCardProps = {
  protocol: Protocol;
  index: number;
};

export function FormulaCard({ protocol, index }: FormulaCardProps) {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (isExpanded) return;

    const el = textRef.current;
    if (!el) return;

    const check = () => setIsTruncated(el.scrollHeight > el.clientHeight);
    check();

    const observer = new ResizeObserver(check);
    observer.observe(el);

    return () => observer.disconnect();
  }, [isExpanded, protocol.type_explanation, protocol.type]);

  const text = protocol.type_explanation || protocol.type;

  const showButton = isTruncated || isExpanded;

  return (
    <div
      className={cn(
        'flex w-65.5 flex-col rounded-[0.75rem] px-5 pt-10 pb-4 transition-all duration-300 ease-out hover:opacity-95 lg:w-101.75 lg:px-10 lg:pt-20 lg:pb-7.5',
        isExpanded ? 'min-h-40 lg:min-h-50' : 'h-40 lg:h-50',
        index % 2 === 0
          ? 'bg-secondary text-secondary-foreground'
          : 'bg-primary text-primary-foreground',
      )}
    >
      <Link
        to={routes.protocols}
        className="flex min-h-0 flex-1 flex-col outline-none focus:ring-0"
      >
        <Heading as="h3" className="shrink-0 text-left whitespace-nowrap">
          {protocol.name}
        </Heading>

        <p
          ref={textRef}
          className={cn(
            'mt-1 shrink-0 overflow-hidden text-sm leading-7.5 transition-[max-height] duration-300 ease-out lg:text-base lg:leading-6',
            !isExpanded && 'line-clamp-1',
            isExpanded && 'max-h-80',
          )}
        >
          {text}
        </p>
      </Link>

      {showButton && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsExpanded((v) => !v);
          }}
          className={cn(
            'mt-2 flex shrink-0 items-center gap-1 self-start rounded text-xs font-medium transition-colors duration-200 outline-none',
            index % 2 === 0
              ? 'text-secondary-foreground/80 hover:text-secondary-foreground'
              : 'text-primary-foreground/80 hover:text-primary-foreground',
          )}
        >
          {isExpanded ? 'Show less' : 'Show more'}
          <ChevronDown
            className={cn(
              'size-3.5 shrink-0 transition-transform duration-300 ease-out',
              isExpanded && 'rotate-180',
            )}
          />
        </button>
      )}
    </div>
  );
}
